import type { SnakeDir, SnakeGame, SnakeStep } from "./engine";

/**
 * «مار» — THE CLOCK. Owns one game's timing; the React screen only wires it (the board's canvas, the
 * pad, the overlays), the way `TableController` owns a table's rules for its room.
 *
 * Phases: `ready` → `countdown` (3, 2, 1 — a second each) → `running` ⇄ `paused` → `over`.
 *
 * ⚠️ AT MOST ONE TIMER EXISTS, AND NONE WHILE PAUSED. Every pause clears it: a backgrounded app must
 * run nothing at all (CLAUDE.md, «A MINI APP MUST PUT ALMOST NO LOAD ON THE PHONE»). No
 * `requestAnimationFrame` and no `setInterval`: the board repaints only when the snake moves.
 *
 * ⚠️ STEPS RUN ON AN ABSOLUTE TIMELINE — the next one is due at `previous + stepMs`, so a late timer
 * does not drift the pace. A tick that fires MORE THAN ONE STEP LATE (a frozen WebView, a long GC)
 * re-anchors the timeline at «now» and takes ONE step: never a burst of catch-up steps, which would
 * move the snake several cells the player never saw.
 *
 * PAUSING is a set of HOLDS, not a flag: `hidden` (the app went to the background) and `dialog` (the
 * «خروج» confirmation). Any hold pauses; the game resumes — always through a fresh 3, 2, 1 — only once
 * every hold is released, so an app that comes back while the dialog is open stays paused until the
 * dialog closes. A hold during the countdown cancels it; the next resume counts from 3 again.
 */

export type SnakePhase = "ready" | "countdown" | "running" | "paused" | "over";

/** Why the game is held: the app is in the background, or the «خروج» confirmation is open. */
export type SnakeHold = "hidden" | "dialog";

/** The timers and the clock, injected so the tests can drive them. */
export interface SnakeClock {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  now(): number;
}

/** The browser's own. */
export const browserClock: SnakeClock = {
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number),
  now: () => performance.now(),
};

/** The countdown before every run and every resume: 3, 2, 1. */
export const SNAKE_COUNTDOWN_FROM = 3;
/** Each number of it stays this long. */
export const SNAKE_COUNTDOWN_MS = 1000;

export interface SnakeControllerHooks {
  /** A new game began: paint the whole board. */
  onStart(game: SnakeGame): void;
  /** The snake moved: paint only what the step changed. */
  onStep(step: SnakeStep, game: SnakeGame): void;
  /** The phase changed. */
  onPhase(phase: SnakePhase): void;
  /** The countdown shows a new number (3, 2, 1). */
  onCountdown(n: number): void;
}

export class SnakeController {
  private current: SnakePhase = "ready";
  private run: SnakeGame | null = null;
  private timer: unknown = null;
  /** When the pending timer was meant to fire, on the clock's own scale. */
  private due = 0;
  private count = 0;
  private readonly holds = new Set<SnakeHold>();

  constructor(
    private readonly opts: {
      clock: SnakeClock;
      /** Milliseconds between two steps — the chosen speed's. */
      stepMs: number;
      /** A fresh game, with the chosen speed's points. */
      newGame: () => SnakeGame;
      hooks: SnakeControllerHooks;
    },
  ) {}

  get phase(): SnakePhase {
    return this.current;
  }

  get game(): SnakeGame | null {
    return this.run;
  }

  /** Whether any hold is set. */
  get held(): boolean {
    return this.holds.size > 0;
  }

  /** «شروع» / «دوباره»: a new game, then the countdown (held: paused until every hold is released). */
  start(): void {
    if (this.current !== "ready" && this.current !== "over") return;
    this.clear();
    this.run = this.opts.newGame();
    this.opts.hooks.onStart(this.run);
    if (this.holds.size > 0) this.setPhase("paused");
    else this.countFrom3();
  }

  /** A tap on the pad. It queues during the countdown too, so the first move can be set early. */
  turn(dir: SnakeDir): void {
    if (this.current === "countdown" || this.current === "running") this.run?.turn(dir);
  }

  /** Hold the game: it pauses (from the countdown or a run) and every timer goes. */
  pause(reason: SnakeHold): void {
    this.holds.add(reason);
    if (this.current === "countdown" || this.current === "running") {
      this.clear();
      this.setPhase("paused");
    }
  }

  /** Release a hold: with none left, a paused game counts 3, 2, 1 and runs on. */
  resume(reason: SnakeHold): void {
    this.holds.delete(reason);
    if (this.current === "paused" && this.holds.size === 0) this.countFrom3();
  }

  /** The screen is going: no timer may outlive it. */
  dispose(): void {
    this.clear();
  }

  private countFrom3(): void {
    this.count = SNAKE_COUNTDOWN_FROM;
    this.setPhase("countdown");
    this.opts.hooks.onCountdown(this.count);
    this.schedule(this.opts.clock.now() + SNAKE_COUNTDOWN_MS, this.countdownTick);
  }

  private readonly countdownTick = (): void => {
    this.timer = null;
    const at = this.onTime(SNAKE_COUNTDOWN_MS);
    this.count -= 1;
    if (this.count > 0) {
      this.opts.hooks.onCountdown(this.count);
      this.schedule(at + SNAKE_COUNTDOWN_MS, this.countdownTick);
      return;
    }
    this.setPhase("running");
    this.schedule(at + this.opts.stepMs, this.stepTick);
  };

  private readonly stepTick = (): void => {
    this.timer = null;
    const game = this.run;
    if (!game) return;
    const at = this.onTime(this.opts.stepMs);
    const step = game.step();
    this.opts.hooks.onStep(step, game);
    if (step.over) {
      this.setPhase("over");
      return;
    }
    this.schedule(at + this.opts.stepMs, this.stepTick);
  };

  /**
   * The time this tick counts as having fired at: when it was due — or, more than one `period` late,
   * now (the timeline re-anchors instead of catching up).
   */
  private onTime(period: number): number {
    const now = this.opts.clock.now();
    return now - this.due > period ? now : this.due;
  }

  private schedule(at: number, fn: () => void): void {
    this.due = at;
    this.timer = this.opts.clock.setTimeout(fn, Math.max(0, at - this.opts.clock.now()));
  }

  private clear(): void {
    if (this.timer !== null) {
      this.opts.clock.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private setPhase(phase: SnakePhase): void {
    if (this.current === phase) return;
    this.current = phase;
    this.opts.hooks.onPhase(phase);
  }
}
